import { EventInput } from '@fullcalendar/core';
import { BoardDTO, TaskDTO } from '@nx-frontend/shared/types';
import { getTaskStartAndEndDates } from './util';

interface SocketData<T> {
  message: T;
  workspaceId: string;
  userId: number;
}

export interface TasksSocketEventsParams {
  listenSocketEvent: <T>(eventName: string, listener: (data: SocketData<T>) => void) => void;
  onTaskCreateEvent: (taskListId: string, task: EventInput) => void;
  onTaskUpdateEvent: (taskListId: string, task: EventInput) => void;
  onTaskRemoveEvent: (taskListId: string, taskId: string) => void;
  onBoardUpdateEvent: (boardId: string, taskListIds?: string[]) => void;
}

/**
 * noteTask:create message type
 */
interface CreateEventMessage {
  before: null;
  after: TaskDTO;
  taskListId: string;
}

/**
 * noteTask:update message type
 */
interface UpdateEventMessage {
  before: TaskDTO | null;
  after: TaskDTO;
  taskListId: string;
}

/**
 * noteTask:remove message type
 */
interface RemoveEventMessage {
  taskListId: string;
  globalId: string;
}

interface BoardChangeMessage {
  after: BoardDTO | null;
  globalId: string;
}

export class TasksSocketEvents {
  params: TasksSocketEventsParams | null = null;
  inited = false;
  static instance: TasksSocketEvents;

  static getInstance() {
    if (this.instance) {
      return this.instance;
    }
    this.instance = new TasksSocketEvents();
    return this.instance;
  }

  init({
    listenSocketEvent,
    onTaskCreateEvent,
    onTaskUpdateEvent,
    onTaskRemoveEvent,
    onBoardUpdateEvent,
  }: TasksSocketEventsParams) {
    this.params = {
      listenSocketEvent,
      onTaskCreateEvent,
      onTaskUpdateEvent,
      onTaskRemoveEvent,
      onBoardUpdateEvent,
    };

    if (!this.inited) {
      this.inited = true;
      this.listen();
    }
  }

  listen() {
    const { listenSocketEvent, onTaskCreateEvent, onTaskUpdateEvent, onTaskRemoveEvent, onBoardUpdateEvent } =
      this.params || {};
    if (!listenSocketEvent) return;

    listenSocketEvent<CreateEventMessage>('noteTask:create', async ({ message }) => {
      const { after: task, taskListId } = message;

      if (task.deadlineDate !== null && task.deadlineDate !== undefined) {
        const { start, end } = getTaskStartAndEndDates(task.showTime, task.deadlineDate);

        onTaskCreateEvent?.(taskListId, {
          id: task.globalId,
          title: task.title,
          start,
          end,
          done: task.done,
          className: task.done ? 'completed-task' : '',
          extendedProps: {
            originalTime: task.deadlineDate ? task.deadlineDate * 1000 : null,
            taskListId,
          },
        });
      }
    });

    listenSocketEvent<UpdateEventMessage>('noteTask:update', async ({ message }) => {
      const { after: task, taskListId } = message;
      const { start, end } = getTaskStartAndEndDates(task.showTime, task.deadlineDate);

      onTaskUpdateEvent?.(taskListId, {
        id: task.globalId,
        title: task.title,
        start: start,
        end,
        done: task.done,
        className: task.done ? 'completed-task' : '',
        extendedProps: {
          originalTime: task.deadlineDate ? task.deadlineDate * 1000 : null,
          taskListId,
        },
      });
    });

    listenSocketEvent<RemoveEventMessage>('noteTask:remove', async ({ message }) => {
      const { taskListId, globalId: taskId } = message;
      onTaskRemoveEvent?.(taskListId, taskId);
    });

    listenSocketEvent<BoardChangeMessage>('board:change', async ({ message }) => {
      const { globalId: boardId, after } = message;
      onBoardUpdateEvent?.(boardId, after?.columnsOrder);
    });
  }
}
